/**
 * Блок «За всё время в ЮниКвесте» (13.10, ПР10 архива 1С; Р11 владельца). Новый блок рядом с существующими:
 * кабинет оператора («Юниверситет» → Grades, свои) и вкладка ОО «Грейды» (выбранный оператор).
 * Свёрнут по умолчанию — данные грузятся только при раскрытии. Игры всего, по годам, филиалам (закрытые —
 * с пометкой) и квестам, деньги (ставки + такси) и первая игра. Деньги, выплаченные через 1С (до 01.03.2026
 * или игры 1С), подписаны «выплачено через 1С». Данные — GET /api/grades/all-time (права решает сервер).
 */
import { useEffect, useState } from "react";
import { api } from "../../api/client";
import { allTimeUrl, dayRu, games, gamesWord, moneyNote, rub, unratedNote, type AllTimeData, type AllTimeMoney } from "./allTimeModel";
import css from "./allTime.module.css";

interface Props {
  /** Без него — свои игры. */
  operatorId?: string;
  /** Отступ сверху (во вкладке ОО блок идёт сразу за строками квестов). */
  spaced?: boolean;
}

export function AllTimeBlock({ operatorId, spaced = false }: Props) {
  const [open, setOpen] = useState(false);
  /** undefined — ещё не грузили / грузится; null — ошибка. */
  const [data, setData] = useState<AllTimeData | null | undefined>(undefined);
  const [err, setErr] = useState("");
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!open || data) return;
    let alive = true;
    api<AllTimeData>(allTimeUrl(operatorId))
      .then((d) => { if (alive) setData(d); })
      .catch((e: unknown) => { if (alive) { setErr(e instanceof Error ? e.message : "нет связи"); setData(null); } });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, operatorId, nonce]);

  return (
    <section className={`${css.card} ${spaced ? css.spaced : ""}`} aria-label="За всё время в ЮниКвесте">
      <button type="button" className={css.head} aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span>За всё время в ЮниКвесте</span>
        <span className={css.chev} aria-hidden="true">{open ? "▾" : "▸"}</span>
      </button>
      {!open ? null : data === undefined ? (
        <p className={css.state}>Загрузка…</p>
      ) : data === null ? (
        <div className={css.state}>
          <p>Не удалось загрузить: {err}</p>
          <button type="button" className={css.retry} onClick={() => { setData(undefined); setNonce((n) => n + 1); }}>Повторить</button>
        </div>
      ) : data.total.games === 0 ? (
        <p className={css.state}>Игр пока нет</p>
      ) : (
        <Body data={data} />
      )}
    </section>
  );
}

function Body({ data }: { data: AllTimeData }) {
  const t = data.total;
  const note = moneyNote(t), unrated = unratedNote(t.unrated);
  return (
    <div className={css.body}>
      <div className={css.sum}>
        <div><b>{t.games}</b><small>{gamesWord(t.games)} всего</small></div>
        <div><b>{rub(t.money)}</b><small>ставки и такси</small></div>
      </div>
      {note ? <p className={css.note1c}>{note}</p> : null}
      {unrated ? <p className={css.meta}>{unrated}</p> : null}
      {data.first ? (
        <p className={css.meta}>Первая игра — {dayRu(data.first.date)}, {data.first.quest_name}{data.first.branch_name ? ` · ${data.first.branch_name}` : ""}</p>
      ) : null}
      <Group title="По годам" rows={data.years.map((y) => ({ key: y.year, name: y.year, m: y }))} />
      <Group title="По филиалам" rows={data.branches.map((b) => ({ key: b.id || "—", name: b.name, tag: b.active ? "" : "закрыт", m: b }))} />
      <Group title="По квестам" rows={data.quests.map((q) => ({ key: q.id, name: q.name, sub: q.branch_name, tag: q.active ? "" : "закрыт", m: q }))} />
    </div>
  );
}

interface GroupRow { key: string; name: string; sub?: string; tag?: string; m: AllTimeMoney }

function Group({ title, rows }: { title: string; rows: GroupRow[] }) {
  if (!rows.length) return null;
  return (
    <div className={css.group}>
      <h4 className={css.gh}>{title}</h4>
      <ul className={css.list}>
        {rows.map((r) => {
          const note = moneyNote(r.m);
          return (
            <li key={r.key} className={css.row}>
              <span className={css.name}>
                {r.name}{r.tag ? <em className={css.tag}>{r.tag}</em> : null}
                {r.sub ? <small>{r.sub}</small> : null}
              </span>
              <span className={css.n}>{games(r.m.games)}</span>
              <span className={css.rub}>{rub(r.m.money)}</span>
              {note ? <small className={css.rowNote}>{note}</small> : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
